"use client";

/**
 * Feature flags as the client may see them: read-only, from
 * GET /api/features. Used only to hide UI - every gated endpoint checks
 * the same flag server-side, so nothing here grants access.
 */
import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";

type Flags = Record<string, boolean>;

let cached: Flags | null = null;
let inflight: Promise<Flags> | null = null;

export function fetchFeatures(): Promise<Flags> {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetch(`${API_BASE}/features`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((f: Flags) => { cached = f; return f; })
      .catch(() => ({} as Flags))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

/** The flags if they have arrived already (a hook's first render), else null. */
export function peekFeatures(): Flags | null {
  return cached;
}

/** `fallback` is what renders before the answer arrives (and when the
 *  request fails). Known flags default to on server-side too. */
export function useFeatureFlag(key: string, fallback = true): boolean {
  const [on, setOn] = useState<boolean>(cached ? cached[key] ?? fallback : fallback);
  useEffect(() => {
    let alive = true;
    fetchFeatures().then((f) => { if (alive && key in f) setOn(Boolean(f[key])); });
    return () => { alive = false; };
  }, [key]);
  return on;
}
